// Boot-time member-roster prime — the single biggest workspace loading-speed
// win for the member list.
//
// PROBLEM (profiled 2026-06-13): the /users/members roster is authed by the SDK
// Mongo token alone (already in localStorage at page entry) and is org-scoped
// SERVER-side from that token — so it needs NEITHER getMe, the governance JWT,
// NOR the resolved org/workspace state. Yet the normal path
// (loadPrivateData → listOrgMembers) is structurally pinned to the END of a
// fully sequential auth+mount boot: awaitSDKInit → getMe → eager
// org/workspace scope fetches → import('smbls') + create() → onCreate prelude →
// subscribeAuthSession → loadPrivateData. The roster fetch can't even START
// until several seconds in (measured: people not on screen until ~7-13s).
//
// FIX: fire the roster fetch at the very workspace entry (index.js), in
// PARALLEL with the entire boot, and have loadPrivateData CONSUME the in-flight
// promise instead of issuing a fresh round-trip at the tail of the boot. This
// collapses the roster's latency from "after the whole boot" to "≈ as soon as
// the SDK init resolves (a few ms)". Zero correctness risk: same endpoint, same
// token, same server-side org scoping — and loadPrivateData falls back to a
// fresh fetch if the prime is absent or failed (worst case == old behavior).

let _primed = null
let _primedOrgSlug = null
let _consumed = false

// The /w/<org-slug> segment at page entry — the ONLY tenant signal available
// before any auth/workspace state resolves. Sent to the server as `orgSlug`
// (membership-verified there) so the prime is scoped to the tab's viewed org
// rather than the token's global activeOrganization, which lags URL-based
// workspace browsing (the prime used to plant the OTHER org's roster).
// Named distinctly from app.js's own unrelated `_urlOrgSlug` — two
// same-named module-scope consts in the same bundle get esbuild-
// disambiguated (`_urlOrgSlug2`), which frank's classifier can't map back
// to source (`unresolvable-identifier`). See tickets/fable.md "decompose
// workspace-shell's 507 defects".
const _primeUrlOrgSlug = () => {
  const m = /^\/w\/([^/]+)/.exec(globalThis.location?.pathname || '')
  return m ? m[1].toLowerCase() : null
}

// Fire the roster fetch immediately. Idempotent — only the first call issues
// the request; later calls return the same in-flight promise. The SDK Proxy
// (wrapWithInitAwait) defers execute() onto the init promise, so calling this
// before the SDK has finished initializing is safe — it runs the moment init
// resolves. Returns a promise of the envelope-unwrapped row array (same shape
// listOrgMembers yields), or null on failure.
export const primePeople = (sdk) => {
  if (_primed || !sdk || typeof sdk.execute !== 'function') return _primed
  _primedOrgSlug = _primeUrlOrgSlug()
  _primed = Promise.resolve()
    .then(() =>
      sdk.execute(
        'users.members',
        'list',
        _primedOrgSlug ? { orgSlug: _primedOrgSlug } : undefined
      )
    )
    .then((rows) =>
      Array.isArray(rows) ? rows : rows?.data || rows?.people || rows?.rows || []
    )
    .catch((err) => {
      console.warn('[peoplePrime] roster prime failed', err?.message || err)
      return null
    })
  return _primed
}

// The org slug the prime was scoped to (null = claim-scoped legacy prime).
// Consumers compare it against the tab's resolved org before trusting the
// primed rows.
export const primedOrgSlug = () => _primedOrgSlug

// Read the primed promise WITHOUT consuming it — used by the shell onCreate to
// seed root.people the moment the shell mounts (~several seconds before
// loadPrivateData runs at the tail of the boot). loadPrivateData still consumes
// it afterwards for its me-status + fan-out logic.
export const peekPrimedPeople = () => _primed

// Hand the primed promise to the FIRST loadPrivateData run, then null it out so
// any subsequent run (e.g. an org switch, which needs a freshly-scoped fetch)
// issues its own request rather than re-using the boot-org snapshot.
export const consumePrimedPeople = () => {
  if (_consumed) return null
  _consumed = true
  return _primed
}
